import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import refreshIcon from "../assets/images/refresh.svg";
import flagIcon from "../assets/images/redFlag.png";

// rebuild - again - again

const TherapistClients = ({
  clientsData,
  fetchClientsData,
  loading: externalLoading,
  onViewHistory,
}) => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocking, setUnlocking] = useState(new Set());
  const [meetingsMap, setMeetingsMap] = useState({});

  // ----------------------------
  // Load + normalize clients
  // ----------------------------
  useEffect(() => {
    if (clientsData?.clients) {
      setClients(clientsData.clients);
      setLoading(false);
    } else if (fetchClientsData && !externalLoading) {
      fetchClientsData(true)
        .then((data) => {
          if (data?.clients) {
            setClients(data.clients);
          } else {
            setClients([]);
          }
        })
        .catch((err) => {
          console.error("TherapistClients.js: error event");
          setError("Failed to load clients");
        })
        .finally(() => setLoading(false));
    } else {
      fetchClients();
    }
  }, [clientsData]);

useEffect(() => {
  let interval;

  const fetchMeetings = async () => {
    try {
      const userId = localStorage.getItem("PlutonicuserID");

      const res = await axiosInstance.get(
        `/api/GetMeetings/${userId}`
      );

      const map = {};

      res.data.forEach((room) => {
        map[room.meetingID] = {
          meetingID: room.meetingID,
          userIds: room.PlutonicuserID,   
          onlineArray: room.online        
        };
      });

            setMeetingsMap(map);
          } catch (err) {
            console.error("TherapistClients.js: error event");
          }
        };

  // run immediately
  fetchMeetings();

  // then every 5 seconds
  interval = setInterval(fetchMeetings, 5000);

  // cleanup (IMPORTANT)
  return () => clearInterval(interval);
}, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.get("/api/summarize");
      setClients(res.data?.clients || []);
    } catch (err) {
      console.error("TherapistClients.js: error event");
      setError("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------
  // Helpers
  // ----------------------------
  const clientHasLockedSessions = (client) =>
    client.sessions?.some((s) => s.locked);

  const handleViewHistory = (client) => {
  // Tell parent to show chat history
  navigate("/clients"); // optional, keeps URL clean
  if (onViewHistory) {
    onViewHistory(client);
  }
};

const getClientOnlineSession = (client) => {
  if (!client?.id) return null;

  for (const meeting of Object.values(meetingsMap)) {
    const index = meeting.userIds?.findIndex(
      (id) => id === client.id
    );

    if (index >= 0 && meeting.onlineArray?.[index]) {
      const roomType = meeting.meetingID
        .split(".")[0]
        .replace("-therapy", "");

      return {
        meetingID: meeting.meetingID,
        roomType,
      };
    }
  }

  return null;
};

const extractRoomId = (name = "") => {
  const n = name.toLowerCase();

  if (n.includes("beach")) return "beach";
  if (n.includes("forest")) return "mountain";
  if (n.includes("park")) return "park";
  if (n.includes("camp")) return "campsite";
  if (n.includes("winter")) return "winter-holiday";

  return null;
};

const handleJoin = (session) => {
  if (!session) return;

  const userId = localStorage.getItem("PlutonicuserID");

  sessionStorage.setItem("meetingID", session.meetingID);
  sessionStorage.setItem("userId", userId);

  navigate(`/unity-room/${session.roomType}`);
};


  const handleUnlock = async (client, sessionID) => {
    setUnlocking((prev) => new Set(prev).add(sessionID));

    // Optimistic update
    setClients((prev) =>
      prev.map((c) =>
        c.id === client.id
          ? {
              ...c,
              sessions: c.sessions.map((s) =>
                s.sessionID === sessionID ? { ...s, locked: false } : s
              ),
            }
          : c
      )
    );

    try {
      await axiosInstance.post(`/api/set-locked-status/${sessionID}`, {
        locked: false,
      });
    } catch (err) {
      console.error("TherapistClients.js: error event");
      alert("Failed to unlock session.");
    } finally {
      setUnlocking((prev) => {
        const next = new Set(prev);
        next.delete(sessionID);
        return next;
      });
    }
  };

  // ----------------------------
  // Loading / error UI
  // ----------------------------
  if (loading || externalLoading) {
    return (
      <div className="bg-white rounded-lg p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        <p className="text-gray-500 mt-2">Loading clients…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg p-6 text-center">
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchClients}
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  // ----------------------------
  // Render
  // ----------------------------
  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between">
        <h3 className="text-lg font-semibold text-blue-600">Clients</h3>
        <button onClick={fetchClients}>
          <img src={refreshIcon} alt="Refresh" className="w-8 h-8" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Nickname</th>
              <th className="px-6 py-3 text-left">Preferred Space</th>
              <th className="px-6 py-3 text-left">Online</th>
              <th className="px-6 py-3 text-left">AI Companion History</th>
              <th className="px-6 py-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {clients.map((client) => (
              <tr key={client.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 flex items-center gap-2">
                  {clientHasLockedSessions(client) && (
                    <img
                      src={flagIcon}
                      className="w-4 h-4"
                      title="Client has locked sessions"
                      alt=""
                    />
                  )}
                  {client.name}
                </td>

                <td className="px-6 py-4">{client.nickname || "—"}</td>

                <td className="px-6 py-4">
                  {client.preferredSpace || "No sessions yet"}
                </td>

                <td className="px-6 py-4">
                  {(() => {
                    const onlineSession = getClientOnlineSession(client);

                    if (!onlineSession) {
                      return <span className="text-gray-400">—</span>;
                    }

                    return (
                      <button
                        onClick={() => handleJoin(onlineSession)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Join
                      </button>
                    );
                  })()}
                </td>

                <td className="px-6 py-4">
                  {client.sessions?.length > 0 ? (
                    <button
                      onClick={() => handleViewHistory(client)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View ({client.sessions.length})
                    </button>
                  ) : (
                    <span className="text-gray-400">No Sessions</span>
                  )}
                </td>

                <td className="px-6 py-4">
                  <button
                    onClick={() => navigate("/clients/schedule", {
                      state: { clientId: client.id, clientEmail: client.email, clientName: client.name },
                    })}
                    className="mr-2 mb-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    Schedule Session
                  </button>
                  {client.sessions?.map(
                    (s) =>
                      s.locked && (
                        <button
                          key={s.sessionID}
                          onClick={() =>
                            handleUnlock(client, s.sessionID)
                          }
                          disabled={unlocking.has(s.sessionID)}
                          className="mr-2 px-3 py-1 bg-green-600 text-white rounded text-sm"
                        >
                          Unlock
                        </button>
                      )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {clients.length === 0 && (
        <div className="p-6 text-center text-gray-500">
          No clients found.
        </div>
      )}
    </div>
  );
};

export default TherapistClients;
